/**
 * <SearchProvider> — a tiny global search-term store.
 *
 * The TopBar owns the input; individual pages subscribe via `useSearch()`
 * and filter their own data client-side. This keeps the input in one place
 * while leaving each page in charge of what "search" means for its rows.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

interface SearchState {
  term: string;
  setTerm: (v: string) => void;
  clear: () => void;
}

const SearchCtx = createContext<SearchState | null>(null);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [term, setTerm] = useState('');
  const value = useMemo<SearchState>(() => ({ term, setTerm, clear: () => setTerm('') }), [term]);
  return <SearchCtx.Provider value={value}>{children}</SearchCtx.Provider>;
}

export function useSearch(): SearchState {
  const ctx = useContext(SearchCtx);
  if (!ctx) throw new Error('useSearch must be used inside <SearchProvider>');
  return ctx;
}

/** Case-insensitive substring match — treats empty term as "match everything". */
export function matchesTerm(haystack: string, term: string): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return haystack.toLowerCase().includes(t);
}
